import { useMemo } from "react";

import get from "lodash/get";

import { prepareRequestKeywordAvgData } from "app/pages/activation/components/RankWidget/helpers/prepareRequestKeywordAvgData";
import { prepareRequestKWTrendData } from "app/pages/activation/components/RankWidget/helpers/prepareRequestKWTrendData";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import useActivationObjectQuery from "app/pages/activation/queries/data/useActivationObjectQuery";
import { useGetIsEnoughCreditsToCreateRTQuery } from "app/pages/activation/queries/rankTracker/useGetIsEnoughCreditsToCreateRTQuery";
import { useGetKeywordsAvgQuery } from "app/pages/activation/queries/rankTracker/useGetKeywordsAvgQuery";
import { useGetKeywordsTrendsQuery } from "app/pages/activation/queries/rankTracker/useGetKeywordsTrendsQuery";
import { useGetRTCampaignQuery } from "app/pages/activation/queries/rankTracker/useGetRTCampaignQuery";

import { mapKeywordAvgSchemaToWidgetData } from "../helpers/mapKeywordAvgSchemaToWidgetData";
import type { IRankTrackerWidgetData } from "../TrafficWidgets/types";

type RankTrackerWidgetData = {
    data: IRankTrackerWidgetData | undefined;
    isLoading: boolean;
    isEmpty: boolean;
    isScraping: boolean;
    isRTAvailable: boolean;
};

type RankTrackerHomepageGates = {
    hasProjects: boolean;
    isActiveActivationInMetadata: boolean;
};

export const useRankTrackerWidgetData = (
    activationId: string | undefined,
    isMetadataLoading: boolean,
    isCompetitorsLoading: boolean,
    { hasProjects, isActiveActivationInMetadata }: RankTrackerHomepageGates,
): RankTrackerWidgetData => {
    const isRtApiEnabled = ActivationComponentAssets.isRankTrackerActivationApiEnabled;

    const canCallActivationRtApis =
        isRtApiEnabled && hasProjects && isActiveActivationInMetadata && Boolean(activationId);

    const { data: isRTWidgetAllowed } = useGetIsEnoughCreditsToCreateRTQuery(activationId, canCallActivationRtApis);

    const { data: activationData, isLoading: isActivationLoading } = useActivationObjectQuery(
        activationId,
        false,
        canCallActivationRtApis,
    );

    const RTCampaignId = get(activationData, "assets.rankTrackerCampaignId", "");
    const isWidgetDisabled = !RTCampaignId;

    const { data: RTCampaignData, isLoading: isRTCampaignLoading } = useGetRTCampaignQuery(
        activationId,
        RTCampaignId,
        canCallActivationRtApis,
    );

    const { data: KeywordsTrendsData, isLoading: isKeywordsTrendsLoading } = useGetKeywordsTrendsQuery(
        prepareRequestKWTrendData(RTCampaignData),
    );

    const keywordsAvgRequestParams = useMemo(() => prepareRequestKeywordAvgData(RTCampaignData), [RTCampaignData]);

    const {
        data: keywordsAvgData,
        isLoading: isKeywordsAvgLoading,
        isFetching: isKeywordsAvgFetching,
    } = useGetKeywordsAvgQuery(keywordsAvgRequestParams);

    const isScraping = !isWidgetDisabled && !get(RTCampaignData, "firstScrapeTime") && !isRTCampaignLoading;

    const rankTrackerWidgetData = useMemo(
        () =>
            mapKeywordAvgSchemaToWidgetData(
                keywordsAvgData,
                get(RTCampaignData, "scrapingConfigurations[0].geoLocationCountry"),
                KeywordsTrendsData,
            ),
        [keywordsAvgData, RTCampaignData, KeywordsTrendsData],
    );

    const isEmpty = (!isKeywordsAvgFetching && !isKeywordsAvgLoading && !rankTrackerWidgetData) || isScraping;

    const isLoading =
        isCompetitorsLoading ||
        isMetadataLoading ||
        isActivationLoading ||
        isRTCampaignLoading ||
        isKeywordsTrendsLoading ||
        isKeywordsAvgLoading ||
        isKeywordsAvgFetching;

    return {
        data: rankTrackerWidgetData ?? undefined,
        isLoading,
        isEmpty,
        isScraping,
        isRTAvailable: canCallActivationRtApis && Boolean(isRTWidgetAllowed),
    };
};
